import React, { Fragment, useState, useEffect } from 'react';
import { 
  Button, Typography, Divider,
  Card, CardHeader, CardContent, 
  Grid, Paper, Box, TextField, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@material-ui/core';
import Breadcrumbs from '../core/controls/components/breadcrumbs';
import { getEnvVariable } from '../modules/environmentalList.js';
import { GetData } from '../core/controls/helpers/requests';
import DefaultSettings from '../core/controls/components/settings';
import Autocomplete from '@material-ui/lab/Autocomplete';
 
const breadcrumbs = {
    type: 'breadcrumbs',
    items: [
        { "link": "Regenerate Invoice", href: "/Administration/regenerateapplicantinvoice", "materialicon": "local_atm", className: 'breadcrumb-item ', class: '' }
    ],
}
 
export default function RegenerateApplicantInvoice(){
    const [isProcessing, setIsProcessing] = useState(false);
    const [applicants, setApplicants] = useState([]);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [invoiceAction, setInvoiceAction] = useState(''); 
    const [lineitemsCount, setlineitemsCount] = useState(0);
    const [isLoadingCounts, setIsLoadingCounts] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingRadioValue, setPendingRadioValue] = useState('');
    const [dialogConfig, setDialogConfig] = useState({
        title: "",
        content: "",
        confirmAction: null
    });
        
    useEffect(() => {
        getownernames();
    }, []);

    const showConfirmation = (title, content, confirmAction) => {
        setDialogConfig({
            title: title,
            content: content,
            confirmAction: confirmAction
        });
        setDialogOpen(true);
    };

    const handleConfirm = () => {
        setDialogOpen(false);
        if (dialogConfig.confirmAction) {
            dialogConfig.confirmAction();
        }
    };

    const handleCancel = () => {
        setDialogOpen(false);
        // Reset pending radio value when canceled
        setPendingRadioValue('');
    };

    const handleRadioChange = (event) => {
        if (!selectedApplicant) {
            DefaultSettings.getAlert()?.show('Please select an applicant first', 'warning');
            return;
        }
        
        const newValue = event.target.value;
        // Apply radio selection directly without confirmation
        applyRadioSelection(newValue);
    };

    const applyRadioSelection = (value) => {
        setIsLoadingCounts(true);
        setInvoiceAction(value);
        
        let url = getEnvVariable('Payment') + 
                `InvoiceCycleRun/GetApplicantLineitemsCount/${selectedApplicant.id}/${value}`;

        GetData(url, '', true).then(resp => {
            if (resp && resp.length > 0) {
                setlineitemsCount(resp.length);
            } else {
                setlineitemsCount(0);
            }
            setIsLoadingCounts(false);
        }).catch(err => {
            console.error("Error fetching applicant data:", err);
            setlineitemsCount(0);
            setIsLoadingCounts(false);
        });
    };
    

    const getownernames = () => {
        setIsProcessing(true);
            let url = getEnvVariable('Payment') + `InvoiceDashboard/AllOwnersList`;
    
            GetData(url, '', true).then(resp => {
                if (resp && resp.length > 0) {
                    const uniqueApplicants = Array.from(new Set(
                        resp.map(item => JSON.stringify({ id: item.ApplicantId, name: item.ApplicantName }))
                    )).map(item => JSON.parse(item));
                    
                    setApplicants(uniqueApplicants);
                }
                setIsProcessing(false);
            }).catch(err => {
                console.error("Error fetching applicant data:", err);
                setIsProcessing(false);
            });
        }
    
        const handleInputSubmit = () => {
            if (selectedApplicant && invoiceAction) {
                // Show confirmation dialog with message based on the selected radio button value
                if (invoiceAction === 'R') {
                    showConfirmation(
                        "Regenerate Invoice",
                        "The current unpaid invoice for the selected applicant will be canceled, and a new invoice will be generated. Please confirm to continue.",
                        generateInvoice
                    );
                } else if (invoiceAction === 'N') {
                    showConfirmation(
                        "Create New Invoice",
                        "This action will generate an invoice exclusively for the newly added permits for the selected applicant. Please confirm to continue.",
                        generateInvoice
                    );
                }
            } else {
                DefaultSettings.getAlert()?.show('Please select an applicant and an invoice action', 'warning');
            }
        };

        const generateInvoice = () => {
            setIsProcessing(true);
            console.log('Processing applicant:', selectedApplicant.id, 'Action:', invoiceAction);
            
            // Modify URL to include invoice action type
            let url = getEnvVariable('Payment') + 
                `InvoiceCycleRun/GenerateApplicantInvoice/${selectedApplicant.id}/${invoiceAction}`;
            
            GetData(url, '', true).then(resp => {
                if (resp) {
                    DefaultSettings.getAlert().show('Applicant invoice generated successfully.', 'info');
                }
                setIsProcessing(false);
                setSelectedApplicant(null);
                setInvoiceAction('');
                setlineitemsCount(0);
            }).catch(err => {
                console.error("Error generating invoice:", err);
                setIsProcessing(false);
                DefaultSettings.getAlert().show('Failed to generate invoice. Please try again.', 'error');
            });
        };


    const handleApplicantChange = (event, newValue) => {
        setSelectedApplicant(newValue);
        // Reset invoice action and line items count when applicant changes
        setInvoiceAction('');
        setlineitemsCount(0);
    };

    return (
        <Fragment>
            <div className="d-flex application-border my-2 p-0">
                <Breadcrumbs {...breadcrumbs} className="pt-2" />
            </div>
            <div className="application-border mb-1">
            <Card>
                <CardHeader 
                    title="Invoice & Permit Management" 
                />
                <Divider />
                <CardContent>
                    <Grid>
                        {/* Owner-specific invoice section */}
                        <Grid item xs={12} md={12} mx-2>
                            <Paper elevation={1}>
                                <Box p={3} className='mx-2'>
                                    <Typography variant="h6" gutterBottom>
                                        Generate Invoice by Applicant
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" paragraph>
                                        Generate invoices for a specific applicant by entering their Name
                                    </Typography>
                                    <Grid container spacing={2} alignItems="flex-end">
                                        <Grid item xs={12} sm={6}>    
                                                <Typography variant="body1" style={{ fontWeight: 600 }}>Applicant Name:</Typography>
                                                <Autocomplete
                                                    id="applicant-select"
                                                    options={applicants}
                                                    loading={setIsProcessing}
                                                    getOptionLabel={(option) => option.name || ""}
                                                    value={selectedApplicant}
                                                    onChange={handleApplicantChange}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Select Applicant"
                                                            variant="outlined"
                                                            margin="normal"
                                                            placeholder="Type to search applicants"
                                                            fullWidth
                                                        />
                                                    )}
                                                />   
                                    <FormControl component="fieldset" style={{ marginTop: 16, marginBottom: 16 }}>
                        <FormLabel component="legend">Invoice Action</FormLabel>
                        <RadioGroup
                            aria-label="invoice-action"
                            name="invoiceAction"
                            value={invoiceAction}
                            onChange={handleRadioChange}
                            row
                        >
                                            <FormControlLabel 
                                                value="R" 
                                                control={<Radio color="primary" />} 
                                                label={
                                                    <span>
                                                        Regenerate invoice
                                                        {isLoadingCounts && invoiceAction === "R" && (
                                                            <span style={{marginLeft: '8px', fontSize: '0.8rem', color: '#666'}}>
                                                                Loading...
                                                            </span>
                                                        )}
                                                        {!isLoadingCounts && invoiceAction === "R" && (
                                                            <span style={{marginLeft: '8px', fontSize: '0.8rem', color: '#666'}}>
                                                                ({lineitemsCount} items)
                                                            </span>
                                                        )}
                                                    </span>
                                                }
                                                disabled={!selectedApplicant}
                                            />
                                            <FormControlLabel 
                                                value="N" 
                                                control={<Radio color="primary" />} 
                                                label={
                                                    <span>
                                                        Create a new invoice for added permits 
                                                        (<span style={{fontSize: '0.8rem', color: 'red'}}>
                                                           If invoice is already paid
                                                        </span>)
                                                        {isLoadingCounts && invoiceAction === "N" && (
                                                            <span style={{marginLeft: '8px', fontSize: '0.8rem', color: '#666'}}>
                                                                Loading...
                                                            </span>
                                                        )}
                                                        {!isLoadingCounts && invoiceAction === "N" && (
                                                            <span style={{marginLeft: '8px', fontSize: '0.8rem', color: '#666'}}>
                                                                ({lineitemsCount} items)
                                                            </span>
                                                        )}
                                                    </span>
                                                }
                                                disabled={!selectedApplicant}
                                            />
                                        </RadioGroup>
                                       </FormControl> 
                                        </Grid>   
                                        <Grid item>
                                            <Button 
                                                variant="contained" 
                                                color="primary" 
                                                onClick={handleInputSubmit}
                                                disabled={!selectedApplicant || !invoiceAction || isProcessing}
                                                fullWidth
                                            >
                                                Generate Applicant Invoice
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            </div>
            <Dialog
                open={dialogOpen}
                onClose={handleCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" 
                    style={{ 
                        backgroundColor: '#fff3e0', 
                        color: '#e65100',
                        fontWeight: 'bold'
                    }}
                >
                    {dialogConfig.title}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: '#fff8e1', padding: '16px 24px 24px' }}>
                    <DialogContentText id="alert-dialog-description" 
                        style={{ 
                            color: '#e65100', 
                            fontWeight: 500 
                        }}
                    >
                        {dialogConfig.content}
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: '#fff8e1', padding: '8px 24px 16px' }}>
                    <Button onClick={handleCancel} 
                        style={{ 
                            color: '#757575' 
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirm} color="primary"
                        variant="contained" 
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}